import React from 'react'
import { NextImage } from '@/components/NextImage'
import { TeamCrestLink } from '@/components/TeamCrestLink'
import { Skeleton } from '@/components/Loading/Skeleton'
import { useImage } from '@/hooks/images/useImage'
import { useTournaments } from '@/hooks/tournaments/useTournaments'
import { useTranslation } from '@/localization/useTranslation'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface MatchPosterCardProps {
  item: any
  handleCardClick: (item: any) => void
  isPlaceholder?: boolean
}

export const MatchPosterCard = (props: MatchPosterCardProps) => {
  const { item, handleCardClick, isPlaceholder } = props
  const { t } = useTranslation()
  const { data: tournaments, isPlaceholderData } = useTournaments()

  const homeTeam = item?.homeTeam
  const awayTeam = item?.awayTeam
  const isHomeFavorite = homeTeam?._id === item?.favoriteTeamId
  const favoriteTeam = isHomeFavorite ? homeTeam : awayTeam
  const opponentTeam = isHomeFavorite ? awayTeam : homeTeam
  // Without `favoriteTeamId` "opponent" is just the home side by default, so
  // there is no club to single out as the one to link to.
  const linkedOpponent = item?.favoriteTeamId && !isPlaceholder ? opponentTeam : undefined

  // Tournament resolution never fails silently: the detail payload's own
  // entity (`tournamentRef`) wins, the tournaments catalog is only a fallback
  // (skipped while it's placeholder data — its fake ids never match), and a
  // name without a resolvable icon still renders as a text chip.
  const tournamentRef = item?.tournamentRef
  const tournamentId = tournamentRef?._id ?? item?.__belong?.Tournament?.[0]
  const catalogTournament = isPlaceholderData
    ? undefined
    : tournaments?.find((tournament) => tournament._id === tournamentId)
  const tournamentIcon = tournamentRef?.icon ?? catalogTournament?.icon
  const tournamentName = tournamentRef?.name ?? item?.tournament ?? catalogTournament?.name

  const { url: opponentFlag } = useImage({ id: opponentTeam?.flag, cut: 'square', scale: '25' })
  const { url: favoriteFlag } = useImage({ id: favoriteTeam?.flag, cut: 'square', scale: '25' })
  const { url: tournamentBadge } = useImage({ id: tournamentIcon, cut: 'square', scale: '25' })

  const isFinished = item?.programmingData?.status === 'finished'

  if (!item) return null

  return (
    <div
      className={classNames({
        [classes.card]: true,
        [classes['card--placeholder']]: isPlaceholder,
      })}
      onClick={() => handleCardClick(item)}
    >
      <div className={classes.poster}>
        <div className={classes.crests}>
          <TeamCrestLink team={linkedOpponent}>
            <NextImage src={opponentFlag} alt={opponentTeam?.name || 'Opponent'} width={93} height={93} />
          </TeamCrestLink>
          <NextImage src={favoriteFlag} alt={favoriteTeam?.name || 'Favorite team'} width={93} height={93} />
        </div>
        {(tournamentIcon || tournamentName) && (
          <div className={classes.tournamentBadge}>
            {tournamentIcon ? (
              <NextImage src={tournamentBadge} alt={tournamentName ? t(tournamentName) : ''} width={60} height={60} />
            ) : (
              <span className={classes.tournamentBadge__name}>{t(tournamentName)}</span>
            )}
          </div>
        )}
        <div className={classes.footer}>
          {isFinished && (
            <div className={classes.status}>
              <span>{t('finished')}</span>
            </div>
          )}
        </div>
      </div>
      {isPlaceholder ? (
        // Placeholder items carry no team names — a bare "vs" line looks broken
        <Skeleton className={classes.titleSkeleton} />
      ) : (
        <p className={classes.title}>
          {homeTeam?.name} {t('vs')} {awayTeam?.name}
        </p>
      )}
    </div>
  )
}
